/**
 * Comprehensive error handling system for UPS Label Manager frontend
 * Provides centralized error handling, user feedback, and error recovery
 */

import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface ErrorDetails {
  error: string;
  message: string;
  error_code?: string;
  status_code?: number;
  request_id?: string;
  timestamp?: string;
  ups_error?: boolean;
  details?: any;
}

export interface ServiceStatus {
  available: boolean;
  degradation_level: 'full_service' | 'limited_service' | 'emergency_mode' | 'offline';
  error_count: number;
  last_check: number;
  fallback_available: boolean;
}

class ErrorHandler {
  private errorCounts: Map<string, number> = new Map();
  private serviceStatus: Map<string, ServiceStatus> = new Map();
  private readonly maxErrorsBeforeNotification = 3;

  /**
   * Handle API errors with comprehensive error processing
   */
  handleApiError(error: AxiosError, context?: string): void {
    const errorDetails = this.extractErrorDetails(error);
    const errorKey = `${errorDetails.status_code}_${errorDetails.error_code}`;
    
    // Track error frequency
    const currentCount = this.errorCounts.get(errorKey) || 0;
    this.errorCounts.set(errorKey, currentCount + 1);

    // Log error details
    console.error(`API Error [${context || 'Unknown'}]:`, {
      ...errorDetails,
      url: error.config?.url,
      method: error.config?.method,
      timestamp: new Date().toISOString()
    });

    // Show user-friendly error message
    this.showUserError(errorDetails, context);

    // Handle service degradation
    if (errorDetails.ups_error) {
      this.handleServiceDegradation(errorDetails);
    }

    // Report to monitoring if error count exceeds threshold
    if (currentCount >= this.maxErrorsBeforeNotification) {
      this.reportToMonitoring(errorDetails, context);
    }
  }

  /**
   * Extract detailed error information from Axios error
   */
  private extractErrorDetails(error: AxiosError): ErrorDetails {
    const response = error.response;
    
    // Check for timeout errors (often appear as 404 or network errors)
    const isTimeout = error.code === 'ECONNABORTED' || 
                     error.message?.toLowerCase().includes('timeout') ||
                     (error.response?.status === 404 && error.config?.timeout);
    
    if (isTimeout) {
      return {
        error: 'Request Timeout',
        message: 'The request took too long to complete. This may happen with large CSV files. Please try with a smaller file or wait a bit longer.',
        status_code: 408, // Request Timeout
        ups_error: false
      };
    }
    
    if (response?.data && typeof response.data === 'object') {
      const data = response.data as any;
      return {
        error: data.error || 'Unknown Error',
        message: data.message || error.message,
        error_code: data.error_code,
        status_code: data.status_code || response.status,
        request_id: data.request_id,
        timestamp: data.timestamp,
        ups_error: data.ups_error || false,
        details: data.details
      };
    }

    // Network or other errors
    return {
      error: 'Network Error',
      message: error.message || 'An unexpected error occurred',
      status_code: error.response?.status || 0,
      ups_error: false
    };
  }

  /**
   * Show user-friendly error messages
   */
  private showUserError(errorDetails: ErrorDetails, context?: string): void {
    const { status_code, error, message, ups_error } = errorDetails;

    let userMessage = message;
    let toastType: 'error' | 'warning' = 'error';

    // Customize message based on error type
    switch (status_code) {
      case 400:
        userMessage = `Invalid request: ${message}`;
        break;
      case 401:
        userMessage = 'Authentication failed. Please log in again.';
        toastType = 'warning';
        break;
      case 403:
        userMessage = 'Access denied. You do not have permission to perform this action.';
        break;
      case 404:
        // Check if this might be a timeout masquerading as 404
        if (message?.toLowerCase().includes('timeout') || message?.toLowerCase().includes('too long')) {
          userMessage = 'Request timeout: The operation took too long. For large CSV files, this is normal. Please try with a smaller file or wait longer.';
          toastType = 'warning';
        } else {
          userMessage = 'The requested resource was not found.';
        }
        break;
      case 408:
        userMessage = 'Request timeout: The operation took too long to complete. For large CSV files with rate calculations, this may take several minutes. Please try again or use a smaller file.';
        toastType = 'warning';
        break;
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        toastType = 'warning';
        break;
      case 500:
        userMessage = ups_error 
          ? 'UPS service is temporarily unavailable. Please try again later.'
          : 'Server error. Please try again later.';
        break;
      case 503:
        userMessage = 'Service temporarily unavailable. Please try again later.';
        toastType = 'warning';
        break;
      default:
        userMessage = message || 'An unexpected error occurred.';
    }

    // Add context if provided
    if (context) {
      userMessage = `${context}: ${userMessage}`;
    }

    // Show toast notification
    if (toastType === 'error') {
      toast.error(userMessage, {
        duration: 5000,
        position: 'top-right'
      });
    } else {
      toast.error(userMessage, {
        duration: 4000,
        position: 'top-right'
      });
    }
  }

  /**
   * Handle service degradation notifications
   */
  private handleServiceDegradation(errorDetails: ErrorDetails): void {
    const serviceName = this.extractServiceName(errorDetails);
    
    if (serviceName) {
      // Update service status
      this.serviceStatus.set(serviceName, {
        available: false,
        degradation_level: 'emergency_mode',
        error_count: this.errorCounts.get(`${errorDetails.status_code}_${errorDetails.error_code}`) || 1,
        last_check: Date.now(),
        fallback_available: true
      });

      // Show degradation notification
      toast.error(
        `UPS ${serviceName} service is experiencing issues. Using fallback mode.`,
        {
          duration: 6000,
          position: 'top-center'
        }
      );
    }
  }

  /**
   * Extract service name from error context
   */
  private extractServiceName(errorDetails: ErrorDetails): string | null {
    const message = errorDetails.message.toLowerCase();
    
    if (message.includes('address')) return 'Address Validation';
    if (message.includes('rate')) return 'Rate Calculation';
    if (message.includes('label')) return 'Label Creation';
    if (message.includes('track')) return 'Tracking';
    
    return null;
  }

  /**
   * Report errors to monitoring system
   */
  private reportToMonitoring(errorDetails: ErrorDetails, context?: string): void {
    // In a real application, this would send to your monitoring service
    console.warn('High error frequency detected:', {
      error: errorDetails.error,
      count: this.errorCounts.get(`${errorDetails.status_code}_${errorDetails.error_code}`),
      context,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle form validation errors
   */
  handleValidationError(errors: Record<string, string[]>): void {
    const errorMessages = Object.entries(errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('\n');

    toast.error(`Validation Error:\n${errorMessages}`, {
      duration: 6000,
      position: 'top-right'
    });
  }

  /**
   * Handle network connectivity issues
   */
  handleNetworkError(): void {
    toast.error('Network connection lost. Please check your internet connection.', {
      duration: 8000,
      position: 'top-center',
      id: 'network-error' // Prevent duplicate toasts
    });
  }

  /**
   * Clear error counts (useful for testing or reset)
   */
  clearErrorCounts(): void {
    this.errorCounts.clear();
  }

  /**
   * Get current service status
   */
  getServiceStatus(serviceName: string): ServiceStatus | null {
    return this.serviceStatus.get(serviceName) || null;
  }

  /**
   * Get all service statuses
   */
  getAllServiceStatuses(): Map<string, ServiceStatus> {
    return new Map(this.serviceStatus);
  }

  /**
   * Check if service is degraded
   */
  isServiceDegraded(serviceName: string): boolean {
    const status = this.serviceStatus.get(serviceName);
    return status ? !status.available || status.degradation_level !== 'full_service' : false;
  }
}

// Global error handler instance
export const errorHandler = new ErrorHandler();

// Error boundary utility functions (React components moved to separate file)
export const createErrorBoundary = () => {
  // This would be implemented as a React component in a separate file
  console.log('Error boundary utility - implement in React component file');
};

// Utility function to wrap API calls with error handling
export function withErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: string
) {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      if (error instanceof AxiosError) {
        errorHandler.handleApiError(error, context);
      } else {
        console.error('Non-API error:', error);
        toast.error('An unexpected error occurred');
      }
      throw error;
    }
  };
}

export default errorHandler;
